/** @jsxRuntime classic */
import { useQuery } from 'react-query';
import { appLog } from '~src/utils/log';
import { DbShareListItem } from '~src/types/db';
import { loadSharedTenders } from '~src/services/api/shares';

type UseSharesParams = Record<string, any>;
type UseSharesOptions = Record<string, any>;

const FIVE_MINUTES = 5 * 60 * 1000;

const isPlainObject = (v: unknown): v is Record<string, unknown> => {
  if (!v || typeof v !== 'object') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
};

// Stable stringify so react-query key does not change when caller passes `{}` literal
const stableStringify = (value: unknown): string => {
  // eslint-disable-next-line @typescript-eslint/ban-types
  const seen = new WeakSet<object>();

  const normalize = (v: unknown): unknown => {
    if (v === null || v === undefined) return v;
    if (typeof v !== 'object') return v;

    // eslint-disable-next-line @typescript-eslint/ban-types
    if (seen.has(v as object)) return '[Circular]';
    // eslint-disable-next-line @typescript-eslint/ban-types
    seen.add(v as object);

    if (Array.isArray(v)) return v.map(normalize);

    if (isPlainObject(v)) {
      const obj = v as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      Object.keys(obj)
        .sort()
        .forEach((k) => {
          out[k] = normalize(obj[k]);
        });
      return out;
    }

    // For Date / Map / Set / class instances - stringify as-is best effort
    return v;
  };

  try {
    return JSON.stringify(normalize(value));
  } catch {
    // fallback: avoid breaking query key
    return String(value);
  }
};

const shouldRetry = (err: any) => {
  const status = err?.response?.status;
  if (status === 401 || status === 403) return false;
  return true;
};

export const useShares = (
  params: UseSharesParams = {},
  options?: UseSharesOptions
) => {
  const paramsKey = stableStringify(params);

  return useQuery<DbShareListItem[]>(
    ['shares', paramsKey],
    async () => {
      appLog('[useShares] fetch /my_shares', { params });
      const data = await loadSharedTenders(null, params);
      appLog('[useShares] ok', {
        count: Array.isArray(data) ? data.length : 0,
      });
      return data;
    },
    {
      keepPreviousData: true,

      staleTime: FIVE_MINUTES,
      refetchInterval: FIVE_MINUTES,
      refetchOnWindowFocus: false,
      refetchOnMount: false,

      retry: (failureCount: number, err: any) => {
        if (!shouldRetry(err)) return false;
        return failureCount < 2;
      },

      ...options,
      onError: (err: any) => {
        appLog('[useShares] error', { err });
        options?.onError?.(err);
      },
    }
  );
};
